import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// components
import { GenericTableHeader } from "@surgia/ui";
import Button from "@surgia/ui/components/button";
import GenericModal from "@surgia/ui/components/genericModal";
// Import from hub-surgia app
import PurchaseRequestTable from "../../../../../hub-surgia/src/containers/tables/purchaseRequest-table";
import PurchaseRequestDetailsModal from "../../../../../hub-surgia/src/containers/Modals/PurchaseRequestDetailsModal";
import { useGetPurchaseRequestListings } from "../../../../../hub-surgia/src/hooks/get/useGetPurchaseRequestListings";
import { useGetBranches } from "../../../../../hub-surgia/src/hooks/useGetBranches";
import TableControls from "@surgia/ui/containers/table/table-controls";

// constants
const ITEMS_PER_PAGE = 8;

// Filters Modal Component
export const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      ...localFilters,
      id: "",
      branches_ids: "",
    });
  };

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* ID Filter */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.id")}
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300"
              placeholder={t("admin.idRange")}
              value={localFilters.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d-]*$/.test(value) || value === "") {
                  setLocalFilters({ ...localFilters, id: value });
                }
              }}
              inputMode="numeric"
              pattern="[\d-]*"
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

// Main Purchase Request Component
const PurchaseRequest: React.FC<{
  tenantId: string;
  branchIds: string;
}> = ({ tenantId, branchIds }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("SUBMITTED");
  const [filters, setFilters] = useState({
    id: "",
    branches_ids: "",
  });
  const [selectedBranch, setSelectedBranch] = useState<string | null>(
    branchIds
  );
  const [selectedPurchaseRequests, setSelectedPurchaseRequests] = useState<
    string[]
  >([]);
  const [selectedPRForDetails, setSelectedPRForDetails] = useState<{
    prId: string | null;
    branchId: string | null;
  } | null>(null);

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  const statusTabs = [
    { label: "Submitted", value: "SUBMITTED" },
    { label: "Drafts", value: "DRAFT" },
    { label: "History", value: "ORDERED" },
  ];

  const { data: branchesDropdown, isLoading: isBranchesLoading } =
    useGetBranches({
      tenant_id: +tenantId,
      component_id: import.meta.env.VITE_PUBLIC_COMPONENT_ID,
      size: 200,
      branches_ids: branchIds ?? "",
    });

  const { data: purchaseRequestListings, isLoading } =
    useGetPurchaseRequestListings({
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: tenantId,
      branchesIds: selectedBranch || "",
      status: status as "SUBMITTED" | "DRAFT" | "ORDERED",
      page: currentPage,
      size: ITEMS_PER_PAGE,
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    });

  useEffect(() => {
    setCurrentPage(0);
  }, [filters.id, status]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const handleViewPRDetails = (prId: string, branchId: string) => {
    setSelectedPRForDetails({ prId, branchId });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow ">
      <GenericTableHeader
        titleTranslationKey="admin.tenantDetails.content.purchaseRequests.title"
        totalCount={purchaseRequestListings?.total || 0}
        showBadge={true}
        customBadgeText={`${purchaseRequestListings?.total || 0}`}
        layout="justified"
        padding="px-4 py-4"
        showBranchSelector={!!(branchIds && branchIds?.includes(","))}
        branches={branchesDropdown?.branches || []}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        isBranchesLoading={isBranchesLoading}
        showAllBranches={true}
        branchPlaceholder="All Branches"
      />
      <TableControls
        status={status}
        setStatus={setStatus}
        search={""}
        setSearch={() => {}}
        openFilters={openFilters}
        setOpenFilters={setOpenFilters}
        statusTabs={statusTabs}
        showFilter={false}
        showSearch={false}
      />

      {/* Purchase Requests Table Section */}
      <PurchaseRequestTable
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        data={purchaseRequestListings?.data || []}
        totalPages={Math.ceil(
          (purchaseRequestListings?.total || 0) / ITEMS_PER_PAGE
        )}
        loading={isLoading}
        showBranchColumn={selectedBranch?.includes(",")}
        selectedPurchaseRequests={selectedPurchaseRequests}
        setSelectedPurchaseRequests={setSelectedPurchaseRequests}
        status={status}
        hasViewPermission={true}
        onViewDetails={handleViewPRDetails}
        sortState={sortState}
        onSort={handleSort}
      />

      {/* Filters Modal */}
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {/* PR Details Modal */}
      <PurchaseRequestDetailsModal
        isSuperAdmin={true}
        open={!!selectedPRForDetails}
        onClose={() => setSelectedPRForDetails(null)}
        purchaseRequestId={selectedPRForDetails?.prId || ""}
        branchId={selectedPRForDetails?.branchId || ""}
        componentId={import.meta.env.VITE_PUBLIC_COMPONENT_ID || ""}
        tenantId={tenantId}
      />
    </div>
  );
};

export default PurchaseRequest;
